import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text, Button, Group, Stack, NumberInput, Switch, Paper, Loader, Center, Divider } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { getCompanyProfile, updateCompanyProfile } from '../../api/client'

/**
 * Configuration > Borrow Policy — ตั้งค่าอยู่ที่ระดับบริษัท (record เดียวใน company/profile
 * เดียวกับที่เก็บโลโก้/ชื่อบริษัท) ไม่ใช่ config module แยกใหม่ ใช้ endpoint เดิมที่มีอยู่แล้ว
 *
 * บังคับใช้จริงแค่ 4 ตัวแรก (maxExtensionCount, maxExtensionDays, maxBorrowDuration,
 * requireApproval เป็น true โดย design ของระบบอยู่แล้ว) — blockIfOverdue/blockIfReserved
 * ใส่ไว้เผื่ออนาคต ยังไม่ผูก logic ใดๆ ตามที่ตกลงกันไว้
 */
export default function BorrowPolicyTab() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [maxExtensionCount, setMaxExtensionCount] = useState<number | ''>('')
  const [unlimitedCount, setUnlimitedCount] = useState(true)
  const [maxExtensionDays, setMaxExtensionDays] = useState<number | ''>(7)
  const [maxBorrowDuration, setMaxBorrowDuration] = useState<number | ''>(30)
  const [requireApproval, setRequireApproval] = useState(true)
  const [blockIfOverdue, setBlockIfOverdue] = useState(false)
  const [blockIfReserved, setBlockIfReserved] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await getCompanyProfile()
      setUnlimitedCount(data.maxExtensionCount == null)
      setMaxExtensionCount(data.maxExtensionCount ?? '')
      setMaxExtensionDays(data.maxExtensionDays ?? 7)
      setMaxBorrowDuration(data.maxBorrowDuration ?? 30)
      setRequireApproval(data.requireApproval ?? true)
      setBlockIfOverdue(data.blockIfOverdue ?? false)
      setBlockIfReserved(data.blockIfReserved ?? false)
    } catch (e: any) {
      // ── 403 = ไม่มีสิทธิ์ ไม่ใช่ความผิดพลาดจริง — แท็บนี้ mount อยู่เบื้องหลังเสมอไม่ว่าจะดูแท็บ
      // ไหนอยู่ก็ตาม (Tabs ของ Mantine ปกติ mount ทุก panel ไว้พร้อมกัน ไม่ใช่แค่แท็บที่ active)
      // ถ้าโชว์ toast แดงตอน 403 คนที่ไม่ใช่ ADMIN/SUPER_ADMIN จะเห็นข้อความ error แปลกๆ ทุกครั้งที่
      // เข้าหน้า Configuration ทั้งที่จริงๆ ระบบทำงานถูกต้องแล้ว (แค่ไม่มีสิทธิ์ดู Borrow Policy) ──
      if (e?.response?.status !== 403) {
        notifications.show({ color: 'red', message: t('common.error', 'Failed to load') })
      }
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateCompanyProfile({
        maxExtensionCount: unlimitedCount ? null : (maxExtensionCount || null),
        maxExtensionDays: maxExtensionDays || 7,
        maxBorrowDuration: maxBorrowDuration || 30,
        requireApproval,
        blockIfOverdue,
        blockIfReserved,
      })
      notifications.show({ color: 'teal', message: t('common.saved', 'Saved') })
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error', 'Failed to save') })
    } finally { setSaving(false) }
  }

  if (loading) return <Center p="xl"><Loader color="teal" /></Center>

  return (
    <Box maw={560}>
      <Text size="sm" c="dimmed" mb="md">
        {t('config.borrowPolicyDesc', 'Set the limits and rules for borrow requests and due-date extensions across your organization.')}
      </Text>

      <Paper withBorder radius="md" p="md" mb="md">
        <Text fw={600} size="sm" mb="xs">{t('config.borrowPolicyExtension', 'Extension Rules')}</Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <Box>
              <Text size="sm">{t('config.maxExtensionCount', 'Max Extension Count')}</Text>
              <Text size="xs" c="dimmed">{t('config.maxExtensionCountHint', 'How many times a single borrow can be extended')}</Text>
            </Box>
            <Group gap="xs">
              <Switch
                checked={unlimitedCount}
                onChange={(e) => setUnlimitedCount(e.currentTarget.checked)}
                label={t('config.unlimited', 'Unlimited')}
              />
              {!unlimitedCount && (
                <NumberInput value={maxExtensionCount} onChange={(v) => setMaxExtensionCount(v === '' ? '' : Number(v))} min={1} w={100} />
              )}
            </Group>
          </Group>

          <Divider />

          <Group justify="space-between">
            <Box>
              <Text size="sm">{t('config.maxExtensionDays', 'Max Extension Days')}</Text>
              <Text size="xs" c="dimmed">{t('config.maxExtensionDaysHint', 'Maximum days per single extension request')}</Text>
            </Box>
            <NumberInput value={maxExtensionDays} onChange={(v) => setMaxExtensionDays(v === '' ? '' : Number(v))} min={1} w={100} suffix={` ${t('common.days', 'days')}`} />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md" mb="md">
        <Text fw={600} size="sm" mb="xs">{t('config.borrowPolicyBorrow', 'Borrow Rules')}</Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <Box>
              <Text size="sm">{t('config.maxBorrowDuration', 'Max Borrow Duration')}</Text>
              <Text size="xs" c="dimmed">{t('config.maxBorrowDurationHint', 'Maximum days from today for the initial due date')}</Text>
            </Box>
            <NumberInput value={maxBorrowDuration} onChange={(v) => setMaxBorrowDuration(v === '' ? '' : Number(v))} min={1} w={100} suffix={` ${t('common.days', 'days')}`} />
          </Group>

          <Divider />

          <Group justify="space-between">
            <Box>
              <Text size="sm">{t('config.requireApproval', 'Require Approval')}</Text>
              <Text size="xs" c="dimmed">{t('config.requireApprovalHint', 'Every borrow and extension request must be approved')}</Text>
            </Box>
            <Switch checked={requireApproval} onChange={(e) => setRequireApproval(e.currentTarget.checked)} disabled />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md" mb="md">
        <Text fw={600} size="sm" mb="xs">{t('config.borrowPolicyReserved', 'Reserved for Future Use')}</Text>
        <Text size="xs" c="dimmed" mb="sm">{t('config.borrowPolicyReservedHint', 'These toggles are not enforced yet — configure now, enable later.')}</Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <Box>
              <Text size="sm">{t('config.blockIfOverdue', 'Block If Overdue')}</Text>
              <Text size="xs" c="dimmed">{t('config.blockIfOverdueHint', 'Not enforced yet')}</Text>
            </Box>
            <Switch checked={blockIfOverdue} onChange={(e) => setBlockIfOverdue(e.currentTarget.checked)} />
          </Group>

          <Divider />

          <Group justify="space-between">
            <Box>
              <Text size="sm">{t('config.blockIfReserved', 'Block If Reserved')}</Text>
              <Text size="xs" c="dimmed">{t('config.blockIfReservedHint', 'Not enforced yet')}</Text>
            </Box>
            <Switch checked={blockIfReserved} onChange={(e) => setBlockIfReserved(e.currentTarget.checked)} />
          </Group>
        </Stack>
      </Paper>

      <Group justify="flex-end">
        <Button leftSection={<IconDeviceFloppy size={16} />} loading={saving} onClick={handleSave} color="teal">
          {t('common.save', 'Save')}
        </Button>
      </Group>
    </Box>
  )
}
